import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { format } from 'date-fns'
import { sv, enUS, nb, da, fi } from 'date-fns/locale'
import { supabase } from '@/integrations/supabase/client.safe'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Badge } from '@/components/ui/badge'
import { Calendar } from '@/components/ui/calendar'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Progress } from '@/components/ui/progress'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'
import { ArrowLeft, Upload, FileText, X, Plus, CalendarIcon, CheckCircle2, Circle, ChevronDown, ChevronRight } from 'lucide-react'
import { useToast } from '@/hooks/use-toast'
import { cn } from '@/lib/utils'
import { useTranslation } from 'react-i18next'
import { MembersManager } from '@/components/MembersManager'
import { MODULES_ORDERED } from '@/lib/modules/module-registry'
import type { FileKey } from '@/lib/modules/types'

interface Organization {
  id: string
  name: string
}

interface UserProfile {
  id: string
  full_name?: string
}

interface NewCasePageProps {
  organization: Organization | null
  profile: UserProfile | null
}

interface FileUpload {
  id: string
  name: string
  size: number
  type: string
  file: File
  uploadDate: Date
  comments: string
  moduleId: string
  documentKey: string
}

interface DocumentDefinition {
  key: string
  name: string
  description: string
  required: boolean
  formats: string[]
}

interface ModuleDefinition {
  id: string
  name: string
  description: string
  documents: DocumentDefinition[]
}

interface DocumentStatus {
  moduleId: string
  documentKey: string
  uploaded: boolean
  fileId?: string
  comments: string
}

interface AuditMember {
  id: string
  user_id: string
  role: string
  added_at: string
  full_name?: string
  email?: string
  avatar_url?: string
}

/**
 * Get file formats for a given file key
 */
const getFileFormats = (fileKey: FileKey): string[] => {
  const formatMap: Record<string, string[]> = {
    'GL_SIE': ['SIE (.se)', 'SIE4 (.si)'],
    'GL_SIE_PREV': ['SIE (.se)', 'SIE4 (.si)'],
    'TAX_ACCOUNT': ['CSV', 'Excel (.xlsx)'],
    'BANK_TX': ['CSV', 'Excel (.xlsx)', 'PDF'],
    'AR_OPEN_ITEMS': ['CSV', 'Excel (.xlsx)'],
    'AP_OPEN_ITEMS': ['CSV', 'Excel (.xlsx)'],
    'VAT_REPORT': ['PDF', 'Excel (.xlsx)'],
    'VAT_TX_DETAIL': ['CSV', 'Excel (.xlsx)'],
    'COA': ['CSV', 'Excel (.xlsx)', 'PDF'],
    'JOURNAL_META': ['CSV', 'Excel (.xlsx)'],
    'VENDOR_MASTER': ['CSV', 'Excel (.xlsx)'],
    'CUSTOMER_MASTER': ['CSV', 'Excel (.xlsx)'],
    'AGI_REPORTS': ['PDF', 'Excel (.xlsx)'],
  };
  return formatMap[fileKey] || ['PDF', 'CSV', 'Excel (.xlsx)'];
};

/**
 * Build modules from MODULES_ORDERED registry with translations
 */
const getModulesFromRegistry = (t: (key: string) => string): ModuleDefinition[] => {
  return MODULES_ORDERED.map(module => {
    // Build required documents
    const requiredDocs: DocumentDefinition[] = module.requiredFiles.map(fileKey => ({
      key: fileKey,
      name: t(`files.${fileKey}.name`) || fileKey,
      description: t(`files.${fileKey}.description`) || '',
      required: true,
      formats: getFileFormats(fileKey)
    }));

    // Build optional documents (combining recommended + optional)
    const optionalFileKeys = [...(module.recommendedFiles || []), ...module.optionalFiles];
    const optionalDocs: DocumentDefinition[] = optionalFileKeys.map(fileKey => ({
      key: fileKey,
      name: t(`files.${fileKey}.name`) || fileKey,
      description: t(`files.${fileKey}.description`) || '',
      required: false,
      formats: getFileFormats(fileKey)
    }));

    return {
      id: module.id,
      name: t(module.nameKey) || module.name,
      description: module.shortDescription,
      documents: [...requiredDocs, ...optionalDocs]
    };
  });
}

export function NewCasePage({ organization, profile }: NewCasePageProps) {
  const navigate = useNavigate()
  const { toast } = useToast()
  const { t, i18n } = useTranslation('common')
  
  // Get appropriate date locale based on current language
  const getDateLocale = () => {
    switch (i18n.language) {
      case 'en': return enUS
      case 'no': return nb
      case 'dk': return da
      case 'fi': return fi
      case 'sv':
      default: return sv
    }
  }
  
  // Get modules from registry with translations
  const modules = getModulesFromRegistry(t)
  
  // Calculate default fiscal year (previous completed year)
  const getDefaultFiscalYear = () => {
    const currentYear = new Date().getFullYear()
    const previousYear = currentYear - 1
    return {
      start: new Date(previousYear, 0, 1), // January 1st of previous year
      end: new Date(previousYear, 11, 31)  // December 31st of previous year
    }
  }
  
  const defaultFiscalYear = getDefaultFiscalYear()
  
  const [formData, setFormData] = useState({
    organizationName: '',
    organizationNumber: '',
    fiscalYearStart: defaultFiscalYear.start,
    fiscalYearEnd: defaultFiscalYear.end,
    notes: '',
    caseAlias: ''
  })
  
  const [files, setFiles] = useState<FileUpload[]>([])
  const [documentStatus, setDocumentStatus] = useState<DocumentStatus[]>([])
  const [expandedModules, setExpandedModules] = useState<string[]>(['data_quality'])
  const [excludedModules, setExcludedModules] = useState<string[]>([])
  // Alias for backward compatibility with older code references
  const selectedModules = expandedModules
  const [loading, setLoading] = useState(false)
  const [members, setMembers] = useState<AuditMember[]>([])

  // Add current user as owner when component mounts
  useEffect(() => {
    if (profile?.id && members.length === 0) {
      const ownerMember: AuditMember = {
        id: `owner_${profile.id}`,
        user_id: profile.id,
        role: 'owner',
        added_at: new Date().toISOString(),
        full_name: profile.full_name
      }
      setMembers([ownerMember])
    }
  }, [profile, members.length])

  // Initialize document status
  useEffect(() => {
    const initialStatus: DocumentStatus[] = []
    modules.forEach(module => {
      module.documents.forEach(doc => {
        initialStatus.push({
          moduleId: module.id,
          documentKey: doc.key,
          uploaded: false,
          comments: ''
        })
      })
    })
    setDocumentStatus(initialStatus)
  }, [])

  const validateOrganizationNumber = (orgNumber: string) => {
    const cleaned = orgNumber.replace(/\D/g, '')
    return cleaned.length === 10
  }

  const toggleModule = (moduleId: string) => {
    setExpandedModules(prev => 
      prev.includes(moduleId) 
        ? prev.filter(id => id !== moduleId)
        : [...prev, moduleId]
    )
  }

  const toggleModuleExclusion = (moduleId: string) => {
    setExcludedModules(prev => 
      prev.includes(moduleId)
        ? prev.filter(id => id !== moduleId)
        : [...prev, moduleId]
    )
  }

  const handleFileUpload = useCallback((event: React.ChangeEvent<HTMLInputElement>, moduleId: string, documentKey: string) => {
    const selectedFiles = Array.from(event.target.files || [])
    
    if (selectedFiles.length === 0) return

    const file = selectedFiles[0]
    const newFile: FileUpload = {
      id: `file_${Date.now()}_${Math.random()}`,
      name: file.name,
      size: file.size,
      type: file.type,
      file,
      uploadDate: new Date(),
      comments: '',
      moduleId,
      documentKey
    }

    setFiles(prev => [...prev.filter(f => !(f.moduleId === moduleId && f.documentKey === documentKey)), newFile])
    
    setDocumentStatus(prev => 
      prev.map(status => 
        status.moduleId === moduleId && status.documentKey === documentKey
          ? { ...status, uploaded: true, fileId: newFile.id }
          : status
      )
    )

    toast({
      title: t('newCase.fileUploaded'),
      description: t('newCase.fileUploadedDesc', { fileName: file.name })
    })
  }, [toast])

  const removeFile = useCallback((fileId: string) => {
    const file = files.find(f => f.id === fileId)
    if (!file) return

    setFiles(prev => prev.filter(f => f.id !== fileId))
    setDocumentStatus(prev => 
      prev.map(status => 
        status.fileId === fileId
          ? { ...status, uploaded: false, fileId: undefined }
          : status
      )
    )

    toast({
      title: t('newCase.fileRemoved'),
      description: t('newCase.fileRemovedDesc', { fileName: file.name })
    })
  }, [files, toast])

  const updateDocumentComments = useCallback((moduleId: string, documentKey: string, comments: string) => {
    setDocumentStatus(prev => 
      prev.map(status => 
        status.moduleId === moduleId && status.documentKey === documentKey
          ? { ...status, comments }
          : status
      )
    )
  }, [])

  const getUploadProgress = useCallback(() => {
    const totalRequired = modules.reduce((acc, module) => {
      if (excludedModules.includes(module.id)) return acc
      return acc + module.documents.filter(doc => doc.required).length
    }, 0)
    const uploadedRequired = documentStatus.filter(status => {
      if (excludedModules.includes(status.moduleId)) return false
      const module = modules.find(m => m.id === status.moduleId)
      const document = module?.documents.find(d => d.key === status.documentKey)
      return document?.required && status.uploaded
    }).length

    return totalRequired > 0 ? (uploadedRequired / totalRequired) * 100 : 0
  }, [documentStatus, excludedModules])

  const getModuleProgress = useCallback((moduleId: string) => {
    const module = modules.find(m => m.id === moduleId)
    if (!module) return 0
    if (excludedModules.includes(moduleId)) return 1 // Excluded modules are considered "complete"

    const requiredDocs = module.documents.filter(doc => doc.required)
    const uploadedDocs = requiredDocs.filter(doc => 
      documentStatus.find(status => 
        status.moduleId === moduleId && 
        status.documentKey === doc.key && 
        status.uploaded
      )
    )

    return requiredDocs.length > 0 ? uploadedDocs.length / requiredDocs.length : 1
  }, [documentStatus, excludedModules])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    // Validation
    if (!formData.organizationName.trim()) {
      toast({
        title: t('newCase.toast.validationError'),
        description: t('newCase.toast.orgNameRequired'),
        variant: "destructive"
      })
      return
    }

    if (!validateOrganizationNumber(formData.organizationNumber)) {
      toast({
        title: t('newCase.toast.validationError'), 
        description: t('newCase.toast.orgNumberInvalid'),
        variant: "destructive"
      })
      return
    }

    if (!formData.fiscalYearStart || !formData.fiscalYearEnd) {
      toast({
        title: t('newCase.toast.validationError'),
        description: t('newCase.toast.fiscalYearRequired'),
        variant: "destructive"
      })
      return
    }

    const requiredUploads = modules.reduce((acc, module) => {
      if (excludedModules.includes(module.id)) return acc
      return acc + module.documents.filter(doc => doc.required).length
    }, 0)
    const completedUploads = documentStatus.filter(status => {
      if (excludedModules.includes(status.moduleId)) return false
      const module = modules.find(m => m.id === status.moduleId)
      const document = module?.documents.find(d => d.key === status.documentKey)
      return document?.required && status.uploaded
    }).length

    // Temporarily disabled for testing - allow creating cases without required documents
    /*
    if (completedUploads < requiredUploads) {
      toast({
        title: t('newCase.toast.validationError'),
        description: `${completedUploads} ${t('newCase.toast.documentsIncomplete', { totalRequired: requiredUploads })}`,
        variant: "destructive"
      })
      return
    }
    */

    setLoading(true)
    
    try {
      // Create audit record
      const { data: audit, error } = await supabase
        .from('audits')
        .insert({
          title: formData.caseAlias || `${formData.organizationName} - ${format(formData.fiscalYearStart, 'yyyy')}`,
          description: formData.notes,
          status: 'pending',
          user_id: profile?.id,
            data_input: {
              organizationName: formData.organizationName,
              organizationNumber: formData.organizationNumber,
              fiscalYearStart: formData.fiscalYearStart.toISOString(),
              fiscalYearEnd: formData.fiscalYearEnd.toISOString(),
              excludedModules: excludedModules,
              files: files.map(f => ({ 
                id: f.id,
                name: f.name, 
                size: f.size, 
                type: f.type, 
                uploadDate: f.uploadDate.toISOString(),
                comments: f.comments,
                moduleId: f.moduleId,
                documentKey: f.documentKey
              })),
              documentStatus: documentStatus.map(status => ({
                moduleId: status.moduleId,
                documentKey: status.documentKey,
                uploaded: status.uploaded,
                fileId: status.fileId || null,
                comments: status.comments
              }))
            }
        })
        .select()
        .single()

      if (error) throw error

      // Add members to the audit
      if (members.length > 1) {
        const memberInserts = members
          .filter(member => member.role !== 'owner') // Owner is already added via user_id
          .map(member => ({
            audit_id: audit.id,
            user_id: member.user_id,
            role: member.role,
            added_by: profile?.id
          }))

        if (memberInserts.length > 0) {
          const { error: membersError } = await supabase
            .from('audit_members')
            .insert(memberInserts)

          if (membersError) {
            console.error('Error adding members:', membersError)
            // Don't throw here, case creation was successful
          }
        }
      }

      toast({
        title: t('newCase.toast.caseCreated'),
        description: t('newCase.toast.caseCreatedDesc', { orgName: formData.organizationName })
      })
      
      navigate(`/cases/${audit.id}`)
      
    } catch (error) {
      console.error('Error creating case:', error)
      toast({
        title: t('errors.generic'),
        description: t('newCase.toast.createError'),
        variant: "destructive"
      })
    } finally {
      setLoading(false)
    }
  }

  const formatFileSize = (bytes: number) => {
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    if (bytes === 0) return '0 Byte'
    const i = parseInt(Math.floor(Math.log(bytes) / Math.log(1024)).toString())
    return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + ' ' + sizes[i]
  }

  return (
    <div className="space-y-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button 
          variant="ghost" 
          onClick={() => navigate('/cases')}
          className="gap-2"
        >
          <ArrowLeft className="h-4 w-4" />
          {t('newCase.back')}
        </Button>
        <div className="flex-1">
          <h1 className="text-3xl font-bold tracking-tight">{t('newCase.title')}</h1>
          <p className="text-muted-foreground">{t('newCase.subtitle')}</p>
        </div>
        <div className="text-right min-w-[120px]">
          <p className="text-sm text-muted-foreground">{t('newCase.progress')}</p>
          <div className="flex items-center gap-2">
            <Progress value={getUploadProgress()} className="w-20" />
            <span className="text-sm font-medium">{Math.round(getUploadProgress())}%</span>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* 1. Grundinformation */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <span className="flex items-center justify-center w-6 h-6 rounded-full bg-primary text-primary-foreground text-sm font-medium">
                1
              </span>
              {t('newCase.basicInfo')}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="organizationName">
                  {t('newCase.orgName')} <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="organizationName"
                  value={formData.organizationName}
                  onChange={(e) => setFormData(prev => ({ ...prev, organizationName: e.target.value }))}
                  placeholder={t('newCase.orgNamePlaceholder')}
                  required
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="organizationNumber">
                  {t('newCase.orgNumber')} <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="organizationNumber"
                  value={formData.organizationNumber}
                  onChange={(e) => {
                    const cleaned = e.target.value.replace(/\D/g, '')
                    if (cleaned.length <= 10) {
                      setFormData(prev => ({ ...prev, organizationNumber: cleaned }))
                    }
                  }}
                  placeholder={t('newCase.orgNumberPlaceholder')}
                  required
                  className={!validateOrganizationNumber(formData.organizationNumber) && formData.organizationNumber 
                    ? "border-red-500" : ""}
                />
                {formData.organizationNumber && !validateOrganizationNumber(formData.organizationNumber) && (
                  <p className="text-xs text-red-500">{t('newCase.orgNumberError')}</p>
                )}
              </div>
            </div>

            <div className="space-y-2">
              <Label>
                {t('newCase.fiscalYear')} <span className="text-red-500">*</span>
              </Label>
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="fiscalYearStart">{t('newCase.startDate')}</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={cn(
                          "w-full justify-start text-left font-normal",
                          !formData.fiscalYearStart && "text-muted-foreground"
                        )}
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {formData.fiscalYearStart ? format(formData.fiscalYearStart, "PPP", { locale: getDateLocale() }) : t('newCase.startDateSelect')}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={formData.fiscalYearStart}
                        onSelect={(date) => setFormData(prev => ({ ...prev, fiscalYearStart: date }))}
                        initialFocus
                        className="pointer-events-auto"
                      />
                    </PopoverContent>
                  </Popover>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="fiscalYearEnd">{t('newCase.endDateLabel')}</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={cn(
                          "w-full justify-start text-left font-normal",
                          !formData.fiscalYearEnd && "text-muted-foreground"
                        )}
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {formData.fiscalYearEnd ? format(formData.fiscalYearEnd, "PPP", { locale: getDateLocale() }) : t('newCase.endDateSelect')}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={formData.fiscalYearEnd}
                        onSelect={(date) => setFormData(prev => ({ ...prev, fiscalYearEnd: date }))}
                        initialFocus
                        className="pointer-events-auto"
                      />
                    </PopoverContent>
                  </Popover>
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="notes">{t('newCase.notes')}</Label>
              <Textarea
                id="notes"
                value={formData.notes}
                onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                placeholder={t('newCase.notesPlaceholder')}
                className="min-h-[80px]"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="caseAlias">{t('newCase.caseAlias')}</Label>
              <Input
                id="caseAlias"
                value={formData.caseAlias}
                onChange={(e) => setFormData(prev => ({ ...prev, caseAlias: e.target.value }))}
                placeholder={t('newCase.caseAliasPlaceholder')}
              />
            </div>
          </CardContent>
        </Card>

        {/* 2. Moduler */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <span className="flex items-center justify-center w-6 h-6 rounded-full bg-primary text-primary-foreground text-sm font-medium">
                2
              </span>
              {t('modules.title')}
            </CardTitle>
            <p className="text-sm text-muted-foreground">
              {t('newCase.modulesDescription')}
            </p>
            <div className="text-sm">
              <span className="font-medium">{t('newCase.progressLabel')} </span>
              <span>{documentStatus.filter(s => {
                if (excludedModules.includes(s.moduleId)) return false
                const module = modules.find(m => m.id === s.moduleId)
                const doc = module?.documents.find(d => d.key === s.documentKey)
                return doc?.required && s.uploaded
              }).length} av {modules.reduce((acc, m) => {
                if (excludedModules.includes(m.id)) return acc
                return acc + m.documents.filter(d => d.required).length
              }, 0)} {t('newCase.documentsRequired')}</span>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {modules.map((module) => {
              const progress = getModuleProgress(module.id)
              const isExpanded = expandedModules.includes(module.id)
              const isExcluded = excludedModules.includes(module.id)
              
              return (
                <Collapsible 
                  key={module.id} 
                  open={isExpanded}
                  onOpenChange={() => toggleModule(module.id)}
                >
                  <Card className={`border-l-4 ${isExcluded ? 'border-l-muted bg-muted/20' : 'border-l-primary'}`}>
                    <CollapsibleTrigger asChild>
                      <CardHeader className="cursor-pointer hover:bg-muted/50 transition-colors">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            {isExpanded ? (
                              <ChevronDown className="h-4 w-4" />
                            ) : (
                              <ChevronRight className="h-4 w-4" />
                            )}
                            <div className="flex-1">
                              <CardTitle className={`text-base ${isExcluded ? 'text-muted-foreground' : ''}`}>
                                {module.name}
                              </CardTitle>
                              <p className="text-sm text-muted-foreground mt-1">
                                {module.description}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {isExcluded ? (
                              <Badge variant="secondary" className="text-muted-foreground">
                                {t('modules.excluded')}
                              </Badge>
                            ) : (
                              <>
                                <Progress value={progress * 100} className="w-20" />
                                <Badge variant={progress === 1 ? "default" : "secondary"}>
                                  {Math.round(progress * module.documents.filter(d => d.required).length)} / {module.documents.filter(d => d.required).length}
                                </Badge>
                              </>
                            )}
                          </div>
                        </div>
                      </CardHeader>
                    </CollapsibleTrigger>
                    
                    <CollapsibleContent>
                      <CardContent className="pt-0">
                        <div className="space-y-4">
                          {/* Module Exclusion Checkbox */}
                          <div className="flex items-center space-x-2 p-3 bg-muted/30 rounded-lg border">
                            <input
                              type="checkbox"
                              id={`exclude-${module.id}`}
                              checked={isExcluded}
                              onChange={() => toggleModuleExclusion(module.id)}
                              className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
                            />
                            <Label htmlFor={`exclude-${module.id}`} className="text-sm font-medium cursor-pointer">
                              {t('newCase.excludeModule')}
                            </Label>
                          </div>
                          {!isExcluded && module.documents.map((document) => {
                            const status = documentStatus.find(s => 
                              s.moduleId === module.id && s.documentKey === document.key
                            )
                            const uploadedFile = files.find(f => 
                              f.moduleId === module.id && f.documentKey === document.key
                            )

                            return (
                              <div key={document.key} className="border rounded-lg p-4 space-y-3">
                                <div className="flex items-start justify-between">
                                  <div className="flex items-center gap-3">
                                    {status?.uploaded ? (
                                      <CheckCircle2 className="h-5 w-5 text-green-600 mt-0.5" />
                                    ) : (
                                      <Circle className="h-5 w-5 text-muted-foreground mt-0.5" />
                                    )}
                                    <div>
                                      <div className="flex items-center gap-2">
                                        <h4 className="font-semibold text-sm">{document.name}</h4>
                                        {document.required && (
                                          <span className="text-red-500 text-xs">*</span>
                                        )}
                                      </div>
                                      <p className="text-xs text-muted-foreground mt-1">
                                        {document.description}
                                      </p>
                                       <div className="text-xs text-muted-foreground mt-1">
                                         <span className="font-medium">{t('newCase.format')}</span> {document.formats.join(', ')}
                                       </div>
                                    </div>
                                  </div>
                                  
                                  <div className="flex items-center gap-2">
                                    {uploadedFile && (
                                      <Button
                                        type="button"
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => removeFile(uploadedFile.id)}
                                        className="text-red-600 hover:text-red-700"
                                      >
                                        <X className="h-4 w-4" />
                                      </Button>
                                    )}
                                    
                                    <div>
                                      <input
                                        type="file"
                                        id={`upload-${module.id}-${document.key}`}
                                        className="hidden"
                                        onChange={(e) => handleFileUpload(e, module.id, document.key)}
                                        accept=".pdf,.xlsx,.xls,.docx,.doc,.csv"
                                      />
                                      <label htmlFor={`upload-${module.id}-${document.key}`}>
                                        <Button
                                          type="button"
                                          variant={status?.uploaded ? "secondary" : "default"}
                                          size="sm"
                                          className="gap-2"
                                          asChild
                                        >
                                           <span>
                                             <Upload className="h-4 w-4" />
                                             {status?.uploaded ? t('newCase.replaceFile') : t('newCase.uploadFile')}
                                           </span>
                                        </Button>
                                      </label>
                                    </div>
                                  </div>
                                </div>

                                {uploadedFile && (
                                  <div className="bg-muted/50 rounded p-2 text-sm">
                                    <div className="flex items-center gap-2">
                                      <FileText className="h-4 w-4 text-muted-foreground" />
                                      <span className="font-medium">{uploadedFile.name}</span>
                                      <span className="text-muted-foreground">({formatFileSize(uploadedFile.size)})</span>
                                    </div>
                                  </div>
                                )}

                                 <div className="space-y-2">
                                   <Label htmlFor={`comment-${module.id}-${document.key}`} className="text-xs">
                                     {t('newCase.commentLabel')}
                                   </Label>
                                   <Input
                                     id={`comment-${module.id}-${document.key}`}
                                     value={status?.comments || ''}
                                     onChange={(e) => updateDocumentComments(module.id, document.key, e.target.value)}
                                     placeholder={t('newCase.commentPlaceholder')}
                                     className="text-sm"
                                   />
                                 </div>
                              </div>
                            )
                          })}
                        </div>
                      </CardContent>
                    </CollapsibleContent>
                  </Card>
                </Collapsible>
              )
            })}
          </CardContent>
        </Card>

        {/* 3. Medlemmar */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <span className="flex items-center justify-center w-6 h-6 rounded-full bg-primary text-primary-foreground text-sm font-medium">
                3
              </span>
              Medlemmar
            </CardTitle>
            <p className="text-sm text-muted-foreground">
              Lägg till teammedlemmar som ska ha tillgång till detta ärende
            </p>
          </CardHeader>
          <CardContent>
            <MembersManager
              members={members}
              onMembersChange={setMembers}
              currentUserId={profile?.id || ''}
              isOwner={true}
              organizationId={organization?.id}
            />
          </CardContent>
        </Card>

        {/* Submit Button */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex justify-end gap-4">
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => navigate('/cases')}
                disabled={loading}
              >
                {t('newCase.cancel')}
              </Button>
              <Button 
                type="submit" 
                disabled={loading || getUploadProgress() < 100}
                className="gap-2"
              >
                {loading ? (
                  <>{t('newCase.creating')}</>
                ) : (
                  <>
                    <Plus className="h-4 w-4" />
                    {t('newCase.createCase')}
                  </>
                )}
              </Button>
            </div>
            
            {getUploadProgress() < 100 && (
              <p className="text-sm text-muted-foreground mt-2 text-center">
                {t('newCase.submitHint')}
              </p>
            )}
          </CardContent>
        </Card>
      </form>
    </div>
  )
}